import React, { useEffect, useState } from 'react';
import { getArtistId } from '../state/ArtistManager.js';
import { 
  getCatalogAnalytics, 
  getPredictionsAnalytics, 
  getGrowthAnalytics, 
  getSpotifyAnalytics, 
  getAccountingAnalytics 
} from '../services/AnalyticsService.js';
import CatalogAnalytics from './analytics/CatalogAnalytics.jsx';
import PredictionsPanel from './analytics/PredictionsPanel.jsx';
import GrowthDashboard from './GrowthDashboard.jsx';
import './AnalyticsPanel.css';

export default function AnalyticsPanel({ user }) {
  const artistId = getArtistId();
  const [analyticsData, setAnalyticsData] = useState({
    catalog: null,
    predictions: null,
    growth: null,
    spotify: null,
    accounting: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (artistId) {
      fetchAllAnalytics();
    }
  }, [artistId]);

  const fetchAllAnalytics = async () => {
    try {
      setLoading(true);
      
      const [catalog, predictions, growth, spotify, accounting] = await Promise.all([
        getCatalogAnalytics(artistId),
        getPredictionsAnalytics(artistId),
        getGrowthAnalytics(),
        getSpotifyAnalytics(),
        getAccountingAnalytics(artistId)
      ]);

      setAnalyticsData({
        catalog,
        predictions,
        growth,
        spotify,
        accounting
      });
    } catch (err) {
      setError(err.message || 'Error fetching analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="professional-container">
        <div className="professional-error">
          ❌ Error: {error}
          <button 
            onClick={fetchAllAnalytics}
            className="professional-button"
            style={{ marginTop: '1rem' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="professional-container">
        <div className="professional-loading">
          Loading comprehensive analytics...
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: '📊 Overview', icon: '📊' },
    { id: 'catalog', label: '🎵 Catalog', icon: '🎵' },
    { id: 'predictions', label: '🤖 AI Predictions', icon: '🤖' },
    { id: 'growth', label: '📈 Growth', icon: '📈' },
    { id: 'spotify', label: '🎧 Spotify', icon: '🎧' },
    { id: 'accounting', label: '💰 Revenue', icon: '💰' }
  ];

  return (
    <div className="professional-container">
      <div className="professional-header">
        <h1>📊 Analytics Dashboard</h1>
        <p>Comprehensive insights across catalog, growth, and revenue metrics</p>
        <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>Artist: {artistId} • User: {user?.username || 'Guest'}</p>
      </div>

      {/* Tab Navigation */}
      <div className="professional-grid-auto" style={{ marginBottom: '2rem' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`professional-button ${activeTab === tab.id ? '' : 'professional-button-secondary'}`}
            style={{ 
              width: '100%',
              padding: '0.75rem',
              fontSize: '0.9rem'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div>
            {/* Quick Stats Overview */}
            <div className="professional-grid-4">
              <div className="professional-metric-card">
                <div className="professional-metric-value">
                  {analyticsData.catalog?.metrics?.totalTracks || 9}
                </div>
                <div className="professional-metric-label">Total Tracks</div>
              </div>
              
              <div className="professional-metric-card">
                <div className="professional-metric-value">
                  {analyticsData.spotify?.monthly_listeners || '2.4K'}
                </div>
                <div className="professional-metric-label">Monthly Listeners</div>
              </div>
              
              <div className="professional-metric-card">
                <div className="professional-metric-value">
                  {analyticsData.growth?.total_streams || '45.7K'}
                </div>
                <div className="professional-metric-label">Total Streams</div>
              </div>
              
              <div className="professional-metric-card">
                <div className="professional-metric-value">
                  ${analyticsData.accounting?.total_revenue || '1,247'}
                </div>
                <div className="professional-metric-label">Total Revenue</div>
              </div>
            </div>

            {/* Performance Insights */}
            <div className="professional-grid-2">
              <div className="professional-card">
                <div className="professional-card-header">
                  <h3 className="professional-card-title">🎯 Performance Insights</h3>
                </div>
                <div className="professional-card-content">
                  <ul className="professional-list">
                    <li className="professional-list-item">
                      <div className="professional-list-item-content">
                        <div className="professional-list-item-title">Stream Growth Rate</div>
                      </div>
                      <div className="professional-list-item-value positive">+23.8%</div>
                    </li>
                    <li className="professional-list-item">
                      <div className="professional-list-item-content">
                        <div className="professional-list-item-title">Revenue per Stream</div>
                      </div>
                      <div className="professional-list-item-value">$0.003</div>
                    </li>
                    <li className="professional-list-item">
                      <div className="professional-list-item-content">
                        <div className="professional-list-item-title">Save Rate</div>
                      </div>
                      <div className="professional-list-item-value">8.7%</div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="professional-card">
                <div className="professional-card-header">
                  <h3 className="professional-card-title">🏆 Top Performing Tracks</h3>
                </div>
                <div className="professional-card-content">
                  <ul className="professional-list">
                    <li className="professional-list-item">
                      <div className="professional-list-item-content">
                        <div className="professional-list-item-title">999,999</div>
                        <div className="professional-list-item-meta">18.5K streams</div>
                      </div>
                      <div className="professional-list-item-value">$55.50</div>
                    </li>
                    <li className="professional-list-item">
                      <div className="professional-list-item-content">
                        <div className="professional-list-item-title">OG Bashment</div>
                        <div className="professional-list-item-meta">12.3K streams</div>
                      </div>
                      <div className="professional-list-item-value">$36.90</div>
                    </li>
                    <li className="professional-list-item">
                      <div className="professional-list-item-content">
                        <div className="professional-list-item-title">Monday</div>
                        <div className="professional-list-item-meta">9.7K streams</div>
                      </div>
                      <div className="professional-list-item-value">$29.10</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'catalog' && analyticsData.catalog && (
          <CatalogAnalytics data={analyticsData.catalog} />
        )}

        {activeTab === 'predictions' && analyticsData.predictions && (
          <PredictionsPanel data={analyticsData.predictions} />
        )}

        {activeTab === 'growth' && analyticsData.growth && (
          <GrowthDashboard data={analyticsData.growth} />
        )}

        {activeTab === 'spotify' && (
          <div className="professional-grid-2">
            <div className="professional-card">
              <div className="professional-card-header">
                <h3 className="professional-card-title">🎧 Spotify Metrics</h3>
              </div>
              <div className="professional-card-content">
                <ul className="professional-list">
                  <li className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">Monthly Listeners</div>
                    </div>
                    <div className="professional-list-item-value">
                      {analyticsData.spotify?.monthly_listeners || '2,400'}
                    </div>
                  </li>
                  <li className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">Total Followers</div>
                    </div>
                    <div className="professional-list-item-value">
                      {analyticsData.spotify?.followers || '1,250'}
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="professional-card">
              <div className="professional-card-header">
                <h3 className="professional-card-title">📈 Performance</h3>
              </div>
              <div className="professional-card-content">
                <ul className="professional-list">
                  <li className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">Stream Growth</div>
                    </div>
                    <div className="professional-list-item-value positive">+15.2%</div>
                  </li>
                  <li className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">Save Rate</div>
                    </div>
                    <div className="professional-list-item-value">8.7%</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'accounting' && (
          <div className="professional-grid-3">
            <div className="professional-card">
              <div className="professional-card-header">
                <h3 className="professional-card-title">💰 Revenue Breakdown</h3>
              </div>
              <div className="professional-card-content">
                <ul className="professional-list">
                  <li className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">Streaming Revenue</div>
                    </div>
                    <div className="professional-list-item-value">
                      ${analyticsData.accounting?.streaming_revenue || '847'}
                    </div>
                  </li>
                  <li className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">Sync Licensing</div>
                    </div>
                    <div className="professional-list-item-value">
                      ${analyticsData.accounting?.sync_revenue || '400'}
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="professional-card">
              <div className="professional-card-header">
                <h3 className="professional-card-title">📊 Performance Metrics</h3>
              </div>
              <div className="professional-card-content">
                <div className="professional-stats">
                  <div className="professional-stat">
                    <div className="professional-stat-value">$0.003</div>
                    <div className="professional-stat-label">Per Stream</div>
                  </div>
                  <div className="professional-stat">
                    <div className="professional-stat-value">23.8%</div>
                    <div className="professional-stat-label">Growth Rate</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="professional-card">
              <div className="professional-card-header">
                <h3 className="professional-card-title">🎯 Goals & Targets</h3>
              </div>
              <div className="professional-card-content">
                <ul className="professional-list">
                  <li className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">Monthly Target</div>
                    </div>
                    <div className="professional-list-item-value">$2,000</div>
                  </li>
                  <li className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">Progress</div>
                    </div>
                    <div className="professional-list-item-value positive">62.4%</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
